import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generates a URL-friendly slug from a string
 */
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/--+/g, '-')     // Replace multiple hyphens with single hyphen
    .trim();
}

/**
 * Performs a fuzzy search on a string
 * Returns true if the query is found in the text with some margin for error
 */
export function fuzzySearch(text: string, query: string): boolean {
  if (!text || !query) return false;
  
  // Convert both to lowercase for case-insensitive matching
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  
  // Exact match check
  if (textLower.includes(queryLower)) return true;
  
  // Check for typos with character skipping (for small queries)
  if (queryLower.length <= 4) {
    // For very short queries, be more strict
    return textLower.includes(queryLower);
  }
  
  // For longer queries, allow for some typos
  let textIndex = 0;
  let queryIndex = 0;
  let mismatches = 0;
  const maxMismatches = Math.floor(queryLower.length / 4) + 1; // Allow more mismatches for longer queries
  
  while (textIndex < textLower.length && queryIndex < queryLower.length) {
    if (textLower[textIndex] === queryLower[queryIndex]) {
      queryIndex++;
    } else {
      mismatches++;
      if (mismatches > maxMismatches) return false;
    }
    textIndex++;
  }
  
  // Check if we matched all characters in the query
  return queryIndex === queryLower.length;
}

/**
 * Validates if all required Firebase configuration variables are present
 */
export function validateFirebaseConfig() {
  const requiredKeys = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID'
  ];

  const missingKeys = requiredKeys.filter(key => !process.env[key]);
  
  return {
    isValid: missingKeys.length === 0,
    missingKeys
  };
}

/**
 * Format a number with commas
 */
export function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Format a date
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Calculate the average of two numbers
 */
export function calculateAverage(a: number, b: number): number {
  return (a + b) / 2;
} 